import React, { useEffect, useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  getCampaignAudience, setCampaignAudience, updateCampaign,
  AudienceScope, DripCampaign,
} from '@/actions/drip-campaigns';
import AudiencePicker, { TreeCat } from './AudiencePicker';

interface Props {
  campaign: DripCampaign;
  tree: TreeCat[];
  onClose: () => void;
  onSaved: () => void;
}

const AudienceModal: React.FC<Props> = ({ campaign, tree, onClose, onSaved }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [scope, setScope] = useState<AudienceScope>('product');
  const [productId, setProductId] = useState<number | null>(null);
  const [productIds, setProductIds] = useState<Set<number>>(new Set());
  const [categoryIds, setCategoryIds] = useState<Set<number>>(new Set());
  const [variantIds, setVariantIds] = useState<Set<number>>(new Set());

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [campaign.id]);

  const load = async () => {
    try {
      setLoading(true);
      const res = await getCampaignAudience(campaign.id);
      const d = res?.data;
      setScope((d?.audience_scope as AudienceScope) || 'product');
      setProductId(d?.product_id_fk ?? null);
      setProductIds(new Set(
        (d?.targets || []).filter(t => t.product_id_fk).map(t => t.product_id_fk as number)
      ));
      setCategoryIds(new Set(
        (d?.targets || []).filter(t => t.category_id_fk).map(t => t.category_id_fk as number)
      ));
      setVariantIds(new Set(
        (d?.targets || []).filter(t => t.variant_id_fk).map(t => t.variant_id_fk as number)
      ));
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Could not load the audience');
    } finally {
      setLoading(false);
    }
  };

  const missing =
    (scope === 'product' && !productId) ||
    (scope === 'products' && productIds.size === 0) ||
    (scope === 'category' && categoryIds.size === 0) ||
    (scope === 'variants' && variantIds.size === 0);

  const save = async () => {
    if (missing) return;
    setSaving(true);
    try {
      // The single product lives on the campaign, not in the audience table, so a
      // 'product' scope has to write it back — otherwise changing the product silently
      // keeps matching the old one.
      if (scope === 'product' && productId !== campaign.product_id_fk) {
        await updateCampaign(campaign.id, { product_id_fk: productId } as Partial<DripCampaign>);
      }
      const targets = scope === 'variants'
        ? Array.from(variantIds).map(variant_id => ({ variant_id }))
        : scope === 'products'
          ? Array.from(productIds).map(product_id => ({ product_id }))
          : scope === 'category'
            ? Array.from(categoryIds).map(category_id => ({ category_id }))
            : [];
      await setCampaignAudience(campaign.id, scope, targets);
      toast.success('Audience updated');
      onSaved();
      onClose();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Could not save the audience');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl max-h-[88vh] flex flex-col">
        <div className="flex items-start justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              Who is this journey for?
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
              {campaign.campaign_name}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="p-10 text-center">
            <Loader2 className="w-6 h-6 animate-spin text-brandColor mx-auto" />
          </div>
        ) : (
          <div className="p-5 overflow-y-auto">
            <AudiencePicker
              tree={tree}
              scope={scope} onScopeChange={setScope}
              productId={productId} onProductIdChange={setProductId}
              productIds={productIds} onProductIdsChange={setProductIds}
              categoryIds={categoryIds} onCategoryIdsChange={setCategoryIds}
              variantIds={variantIds} onVariantIdsChange={setVariantIds}
            />
            {missing && (
              <p className="text-xs text-red-600 dark:text-red-400 mt-3">
                {scope === 'product'
                  ? 'Choose a product.'
                  : `Pick at least one ${
                      scope === 'products' ? 'product'
                        : scope === 'variants' ? 'SKU' : 'category'
                    } — an empty audience would never match anything.`}
              </p>
            )}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving || loading || missing}
            className="px-4 py-2 text-sm rounded-lg bg-brandColor text-white hover:opacity-90 disabled:opacity-60"
          >
            {saving ? 'Saving…' : 'Save audience'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AudienceModal;
